import { OpenAPI } from "@propertelligent/client-api";
import { UserLoginData } from "../../common/charts/Enums";
import { EmailProviderType } from "../../../utils/emailProviderType";
export { EmailProviderType };

/**
 * Raw-fetch bridge to the new personal-SMTP/POP3 endpoints on GmailController (provider-status,
 * save-smtp-settings, test-smtp-connection, list-inbox-messages, inbox-message, send-smtp-email).
 * These aren't in the generated @propertelligent/client-api yet — mirrors organizationEmailApi.ts's pattern.
 * The existing Gmail endpoints (send/list/disconnect/etc.) already have generated CommunicationService methods
 * and are used directly from emailview.tsx/Compose.tsx/ShowEmail.tsx — not duplicated here.
 */

export interface PersonalEmailStatus {
  isConnected: boolean;
  providerType: EmailProviderType;
  emailAddress: string | null;
  smtpHost: string | null;
  popHost: string | null;
  username: string | null;
  fromName: string | null;
  fromEmail: string | null;
}

export interface PersonalSmtpSettingsRequest {
  smtpHost: string;
  smtpPort: number;
  popHost: string;
  popPort: number;
  username: string;
  password: string;
  useSmtpSsl: boolean;
  usePopSsl: boolean;
  fromName: string;
  fromEmail: string;
}

export interface InboxMessageSummary {
  uid: string;
  from: string | null;
  fromName: string | null;
  fromEmail: string | null;
  subject: string | null;
  displayDate: string | null;
  date: string | null;
}

export interface InboxMessage extends InboxMessageSummary {
  id: string;
  body: string | null;
  htmlBody: string | null;
  isImportant: boolean;
  isStarred: boolean;
}

/**
 * Formats a UTC ISO date string for display in the viewer's local timezone. The backend's own DisplayDate field
 * is computed server-side against DateTime.UtcNow with no timezone conversion (h:mm tt on the raw UTC value) --
 * fine for a server log, wrong for a user-facing time ("11:50" in Gmail's own UI showed as "6:20 AM" here for an
 * IST user, exactly a 5:30 offset). Recompute it here instead, mirroring the same today/this-year/older shape.
 */
export const formatLocalDisplayDate = (isoDate: string | null | undefined): string => {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  }
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const authHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem(UserLoginData.token)}`,
});

const jsonHeaders = () => ({
  "Content-Type": "application/json",
  ...authHeaders(),
});

/**
 * Fetch which personal email provider (Gmail or custom SMTP/POP3) the current user has connected, if any.
 * Throws on a non-ok response or a network/parse failure — unlike most helpers in this file, this one is
 * deliberately NOT swallowed to a disconnected-looking fallback, because "the status request failed" and
 * "nothing is connected" need distinct UI (a stale/misleading connect screen vs. a real error state). Callers
 * must catch and render their own error state — see emailview.tsx's `loadProviderStatus`.
 */
export const getProviderStatus = async (): Promise<PersonalEmailStatus> => {
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/provider-status`, {
    method: "GET",
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw res;
  }
  const body = await res.json();
  return {
    isConnected: !!body?.success?.isConnected,
    providerType: body?.success?.providerType ?? EmailProviderType.None,
    emailAddress: body?.success?.emailAddress ?? null,
    smtpHost: body?.success?.smtpHost ?? null,
    popHost: body?.success?.popHost ?? null,
    username: body?.success?.username ?? null,
    fromName: body?.success?.fromName ?? null,
    fromEmail: body?.success?.fromEmail ?? null,
  };
};

/** Persist the user's custom SMTP/POP3 settings. Throws the non-ok response on failure. */
export const saveSmtpSettings = async (request: PersonalSmtpSettingsRequest): Promise<void> => {
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/save-smtp-settings`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify(request),
  });
  if (!res.ok) {
    throw res;
  }
};

/** Send a live test email using the given (not-yet-saved) settings, without persisting them. */
export const testSmtpConnection = async (request: PersonalSmtpSettingsRequest): Promise<void> => {
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/test-smtp-connection`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify(request),
  });
  if (!res.ok) {
    throw res;
  }
};

/** List the most recent POP3 inbox messages (headers only, no body). */
export const listInboxMessages = async (maxResults = 25): Promise<InboxMessageSummary[]> => {
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/list-inbox-messages?maxResults=${maxResults}`, {
    method: "GET",
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw res;
  }
  const body = await res.json();
  return body?.success ?? [];
};

/** Fetch one full POP3 message (including body) by its stable UID. */
export const getInboxMessage = async (uid: string): Promise<InboxMessage | null> => {
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/inbox-message?uid=${encodeURIComponent(uid)}`, {
    method: "GET",
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw res;
  }
  const body = await res.json();
  return body?.success ?? null;
};

/** Send a new email through the connected personal SMTP account (no reply/forward — see Rule 12 scope note). */
export const sendPersonalEmail = async (to: string, subject: string, body: string): Promise<void> => {
  const params = new URLSearchParams({ To: to, Subject: subject, Body: body });
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/send-smtp-email?${params.toString()}`, {
    method: "POST",
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw res;
  }
};

/**
 * Exchange the Microsoft (Azure AD / Outlook) OAuth authorization code for a stored personal
 * connection. Raw-fetch bridge — mirrors saveOutlookRequestToken in organizationEmailApi.ts (org-level)
 * and this file's own saveSmtpSettings for the "not in generated client-api yet" precedent. Rejections
 * propagate so the OAuth-return handler can log them before navigating back.
 */
export const saveOutlookRequestToken = async (code: string, redirectUri: string): Promise<void> => {
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/save-outlook-request-token`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify({
      authorizationToken: code,
      redirectUri,
    }),
  });
  if (!res.ok) {
    throw res;
  }
};

/** Send a new email through the connected personal Outlook account (no reply/forward in Phase 1). */
export const sendPersonalOutlookEmail = async (to: string, subject: string, body: string): Promise<void> => {
  const params = new URLSearchParams({ To: to, Subject: subject, Body: body });
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/send-outlook-email-message?${params.toString()}`, {
    method: "POST",
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw res;
  }
};

/**
 * Fetch the connected Outlook account's email address (via Microsoft Graph `/me`), or `null` if not
 * connected / the request fails — mirrors fetchGmailConnectedEmail's swallow-to-null pattern in
 * emailview.tsx since Outlook has no separate profile-status split the way Gmail does.
 */
export const getOutlookProfile = async (): Promise<string | null> => {
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/outlook-profile`, {
    method: "GET",
    headers: authHeaders(),
  });
  if (!res.ok) {
    return null;
  }
  const body = await res.json();
  return body?.success ?? null;
};

/**
 * Phase 2 — personal Outlook inbox parity (list/read/reply/forward/delete/star/counts/contacts), all via
 * Microsoft Graph on the backend (OutlookMessageProvider). These 10 endpoints are brand-new on GmailController
 * and are not in the generated @propertelligent/client-api yet — same raw-fetch bridge precedent as the SMTP/
 * Phase-1-Outlook functions above. Swap to the generated CommunicationService methods once client-api publishes.
 */

export interface OutlookMessageSummary {
  id: string;
  fromName: string | null;
  fromEmail: string | null;
  subject: string | null;
  displayDate: string | null;
  date: string | null;
  isStarred: boolean;
  isImportant: boolean;
}

export interface OutlookMessage extends OutlookMessageSummary {
  body: string | null;
  htmlBody: string | null;
}

export interface OutlookMessageListResult {
  messages: OutlookMessageSummary[];
  nextPageToken: string | null;
}

export interface OutlookEmailCounts {
  inbox: number;
  sent: number;
  starred: number;
}

export interface Contact {
  name: string | null;
  email: string | null;
  phoneNumber: string | null;
}

/**
 * List the connected Outlook mailbox's messages with real server-side paging (unlike POP3's flat single fetch).
 * `folder` ("Inbox"/"Sent"/"Starred", matching emailview.tsx's `filterOptions` pill titles) is an interim
 * extension beyond the plan's originally documented `query`/`maxResults`/`nextPageToken` params -- without it,
 * the Sent/Starred pills would trigger an identical no-op refetch of the Inbox. Needs reconciliation against
 * the actual backend `list-outlook-messages` implementation once merged (see /implementation-validation).
 */
export const listOutlookMessages = async (
  maxResults: number,
  query = "",
  nextPageToken?: string,
  folder?: string
): Promise<OutlookMessageListResult> => {
  const params = new URLSearchParams({ maxResults: String(maxResults), query });
  if (nextPageToken) params.set("nextPageToken", nextPageToken);
  if (folder) params.set("folder", folder);
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/list-outlook-messages?${params.toString()}`, {
    method: "GET",
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw res;
  }
  const body = await res.json();
  return {
    messages: body?.success?.emailMessages ?? [],
    nextPageToken: body?.success?.nextPageToken ?? null,
  };
};

/** Fetch one full Outlook message (including body) by its Graph message id. */
export const getOutlookMessage = async (emailId: string): Promise<OutlookMessage | null> => {
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/outlook-message?emailId=${encodeURIComponent(emailId)}`, {
    method: "GET",
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw res;
  }
  const body = await res.json();
  return body?.success ?? null;
};

/** Delete an Outlook message by its Graph message id. */
export const deleteOutlookMessage = async (emailId: string): Promise<void> => {
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/delete-outlook-message?emailId=${encodeURIComponent(emailId)}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw res;
  }
};

/** Toggle an Outlook message's importance flag. */
export const toggleOutlookMessageImportance = async (emailId: string, isImportant: boolean): Promise<void> => {
  const params = new URLSearchParams({ emailId, isImportant: String(isImportant) });
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/toggle-outlook-message-importance?${params.toString()}`, {
    method: "POST",
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw res;
  }
};

/** Toggle an Outlook message's starred (Graph "flag") state. */
export const toggleOutlookMessageStarred = async (emailId: string, isStarred: boolean): Promise<void> => {
  const params = new URLSearchParams({ emailId, isStarred: String(isStarred) });
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/toggle-outlook-message-starred?${params.toString()}`, {
    method: "POST",
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw res;
  }
};

/** Reply to an Outlook message. Graph auto-targets the original sender — no `to` param needed. */
export const replyOutlookMessage = async (emailId: string, body: string): Promise<void> => {
  const params = new URLSearchParams({ EmailId: emailId, Body: body });
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/reply-outlook-message?${params.toString()}`, {
    method: "POST",
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw res;
  }
};

/** Forward an Outlook message to one or more recipients (comma-separated). */
export const forwardOutlookMessage = async (emailId: string, to: string, body: string): Promise<void> => {
  const params = new URLSearchParams({ EmailId: emailId, To: to, Body: body });
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/forward-outlook-message?${params.toString()}`, {
    method: "POST",
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw res;
  }
};

/**
 * Fetch Outlook mailbox counts. No `important` field — Outlook's importance isn't cheaply countable via
 * Graph (see the UI plan's Open Questions) — the `filterOptions` pills for Outlook are Inbox/Sent/Starred only.
 */
export const getOutlookEmailCounts = async (): Promise<OutlookEmailCounts> => {
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/outlook-email-counts`, {
    method: "GET",
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw res;
  }
  const body = await res.json();
  return {
    inbox: body?.success?.inbox ?? 0,
    sent: body?.success?.sent ?? 0,
    starred: body?.success?.starred ?? 0,
  };
};

/** Fetch the Outlook account's top contacts (Graph People API). */
export const getOutlookContacts = async (): Promise<Contact[]> => {
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/outlook-contacts`, {
    method: "GET",
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw res;
  }
  const body = await res.json();
  return body?.success ?? [];
};

/** Search the Outlook account's contacts (Graph People API). */
export const searchOutlookContacts = async (query: string): Promise<Contact[]> => {
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/outlook-search-contacts?query=${encodeURIComponent(query)}`, {
    method: "GET",
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw res;
  }
  const body = await res.json();
  return body?.success ?? [];
};

/**
 * Fetch the Azure AD application (client) id used to build the Microsoft OAuth consent URL. Raw-fetch
 * bridge (mirrors organizationEmailApi.ts's getMicrosoftClientId) — swap to the generated
 * IntegrationsService method once client-api is regenerated. Resolves to an empty string on failure.
 */
export const getMicrosoftClientId = async (): Promise<string> => {
  try {
    const res = await fetch(`${OpenAPI.BASE}/api/Integrations/keys/microsoft-client-id`, {
      method: "POST",
      headers: authHeaders(),
    });
    if (!res.ok) {
      throw res;
    }
    const body = await res.json();
    return body?.success ?? "";
  } catch (error) {
    console.error("Failed to load Microsoft client id:", error);
    return "";
  }
};
